import { colors } from '@/constants/colors';
import IconLib from '@/utils/IconsLib';
import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import LoginInput from './LoginInput';
import ButtonSignInSignup from './ButtonSignup';
import { verifyEmail } from '@/api/auth';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useAuthStore from '@/store/authStore';
import { resolvePostLoginPath } from '@/utils/sellerReturn';

interface ApiError {
   response?: {
      data?: {
         message?: string;
         errors?: string[];
         err?: string;
      };
   };
}

interface Props {
   handleForm: (form: string) => void;
}

const VerifyEmail = ({ handleForm }: Props) => {
   const navigate = useNavigate();
   const location = useLocation();
   const { setAuth } = useAuthStore();
   const [searchParams] = useSearchParams();
   const userId = searchParams.get('id') || '';

   const [values, setValues] = useState({ otp: '' });
   const [error, setError] = useState('');

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues({ ...values, [e.target.name]: e.target.value });
   };

   const { mutate, isPending } = useMutation({
      mutationFn: verifyEmail,
      onSuccess: (response) => {
         if (response.status === 200) {
            const { token, user } = response.data;
            setAuth(token, user || { email: response.data.user.email });
            // A brand-new buyer arriving from a seller link finishes here, so
            // this path matters as much as sign-in — see utils/sellerReturn.
            navigate(resolvePostLoginPath(location.state?.from), {
               replace: true,
            });
         }
      },
      onError: (err: ApiError) => {
         const errorMessage =
            err.response?.data?.err ||
            err.response?.data?.message ||
            'Verify email failed. Please try again.';
         setError(errorMessage);
         toast.error(errorMessage);
      },
   });

   const handleSubmit = async () => {
      if (isPending) return;
      const { otp } = values;
      if (!otp) {
         setError('Please enter 6 digits OTP');
         toast.error('Please enter 6 digits OTP');
         return;
      }
      setError('');
      mutate({ token: otp, userId });
   };

   return (
      <Wrapper>
         <Bg />
         <Blob $top="8%" $left="10%" $delay="0s" />
         <Blob $top="70%" $left="78%" $delay="3s" $lime />
         <Blob $top="35%" $left="55%" $delay="6s" />
         <Top>
            <BrandRow>
               <BrandBadge>
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                     <path d="M5 12.55a11 11 0 0 1 14 0" />
                     <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                     <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                     <circle cx="12" cy="20" r="1.5" fill="white" stroke="none" />
                  </svg>
               </BrandBadge>
               <Logo>SmartFi</Logo>
            </BrandRow>
            <Nav />
         </Top>
         <div className="container">
            <Content>
               <HeadingBlock>
                  <Tagline>WiFi Vendo · Reimagined</Tagline>
                  <Heading>
                     Come in,
                     <br />
                     <HeadingAccent>SmartFi is free.</HeadingAccent>
                  </Heading>
               </HeadingBlock>
               <LoginBox>
                  <Title>Verify your email</Title>

                  <CheckList>
                     <CheckItem>
                        <IconLib icon="mail-line" size="22px" color="#D2FD58" />
                        <CheckLabel>
                           Check your inbox for the 6-digit code we just sent.
                        </CheckLabel>
                     </CheckItem>
                  </CheckList>

                  <LoginActionContent>
                     <LoginInput
                        title="Enter 6 digits OTP"
                        subtitle=""
                        type="text"
                        hasIcon={true}
                        icon={!values.otp ? 'error-warning-fill' : undefined}
                        value={values.otp}
                        name="otp"
                        onChange={handleChange}
                     />
                  </LoginActionContent>

                  {error && <ErrorMessage>{error}</ErrorMessage>}

                  <PrimaryCTAWrap onClick={handleSubmit}>
                     <ButtonSignInSignup
                        title="Verify"
                        isLoading={isPending}
                        color={colors.PRIMARY}
                     />
                  </PrimaryCTAWrap>

                  <NewHereWrapper>
                     <NewHereLabel>Already have an account?</NewHereLabel>
                     <NewHereLink onClick={() => handleForm('signin')}>
                        Sign in
                     </NewHereLink>
                  </NewHereWrapper>

                  <PolicyWrapper>
                     <PolicyDescription>
                        By continuing, you confirm you are 18 or over and agree
                        to our <PolicyLink href="#">Privacy Policy</PolicyLink>{' '}
                        and <PolicyLink href="#">Terms of Use.</PolicyLink>
                     </PolicyDescription>
                  </PolicyWrapper>
               </LoginBox>
            </Content>
         </div>
      </Wrapper>
   );
};

const bgShift = keyframes`
   0%, 100% { background-position: 0% 50%; }
   50% { background-position: 100% 50%; }
`;
const float = keyframes`
   0%, 100% { transform: translate(0, 0) scale(1); }
   50% { transform: translate(20px, -30px) scale(1.1); }
`;

const Wrapper = styled.div`
   min-height: 100vh;
   position: relative;
   overflow: hidden;
   color: white;
`;

const Bg = styled.div`
   position: fixed;
   inset: 0;
   background: linear-gradient(135deg, #0b1030 0%, #1a1f4a 35%, #2a2165 70%, #0f1535 100%);
   background-size: 200% 200%;
   animation: ${bgShift} 16s ease infinite;
   z-index: 0;
`;

const Blob = styled.div<{ $top: string; $left: string; $delay: string; $lime?: boolean }>`
   position: fixed;
   top: ${({ $top }) => $top};
   left: ${({ $left }) => $left};
   width: 320px;
   height: 320px;
   border-radius: 50%;
   filter: blur(60px);
   background: ${({ $lime }) =>
      $lime
         ? 'radial-gradient(circle, rgba(210,253,88,0.25) 0%, rgba(210,253,88,0) 70%)'
         : 'radial-gradient(circle, rgba(55,96,249,0.35) 0%, rgba(55,96,249,0) 70%)'};
   animation: ${float} 12s ease-in-out infinite;
   animation-delay: ${({ $delay }) => $delay};
   z-index: 0;
   pointer-events: none;
`;

const Top = styled.div`
   position: relative;
   z-index: 2;
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 14px 1.25rem;
   background: rgba(255, 255, 255, 0.03);
   backdrop-filter: blur(8px);
   border-bottom: 1px solid rgba(255, 255, 255, 0.06);

   @media (max-width: 480px) {
      padding: 12px 1rem;
   }
`;

const BrandRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;

const BrandBadge = styled.div`
   width: 34px;
   height: 34px;
   border-radius: 10px;
   background: linear-gradient(135deg, #3760f9 0%, #6a4dff 100%);
   display: grid;
   place-items: center;
   box-shadow: 0 4px 14px rgba(55, 96, 249, 0.45);
   svg {
      width: 20px;
      height: 20px;
   }
`;

const Logo = styled.h1`
   font-weight: 800;
   color: white;
   font-size: 22px;
   letter-spacing: 0.02em;
   background: linear-gradient(90deg, #ffffff 0%, #D2FD58 120%);
   -webkit-background-clip: text;
   -webkit-text-fill-color: transparent;
   background-clip: text;

   @media (max-width: 480px) {
      font-size: 1.25rem;
   }
`;

const Nav = styled.nav``;

const Content = styled.div`
   position: relative;
   z-index: 1;
   padding: 4rem 1rem 3rem;
   display: grid;
   place-items: center;
   grid-template-columns: 1fr 450px;
   gap: 3rem;

   @media (max-width: 1024px) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
      padding: 2.5rem 1rem;
   }
   @media (max-width: 480px) {
      padding: 1.5rem 0.75rem;
      gap: 1rem;
   }
`;

const HeadingBlock = styled.div`
   text-align: left;
   @media (max-width: 1024px) {
      text-align: center;
   }
`;

const Tagline = styled.span`
   display: inline-block;
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.2em;
   text-transform: uppercase;
   padding: 5px 12px;
   border-radius: 999px;
   background: rgba(210, 253, 88, 0.12);
   color: #D2FD58;
   border: 1px solid rgba(210, 253, 88, 0.35);
   margin-bottom: 18px;
`;

const Heading = styled.h1`
   font-size: 56px;
   font-weight: 800;
   color: white;
   line-height: 1.05;
   letter-spacing: -0.01em;

   @media (max-width: 1024px) {
      font-size: 42px;
   }
   @media (max-width: 768px) {
      font-size: 34px;
   }
   @media (max-width: 480px) {
      font-size: 28px;
   }
`;

const HeadingAccent = styled.span`
   background: linear-gradient(90deg, #D2FD58 0%, #7de8ff 100%);
   -webkit-background-clip: text;
   -webkit-text-fill-color: transparent;
   background-clip: text;
`;

const LoginBox = styled.div`
   position: relative;
   padding: 1.75rem;
   width: 450px;
   border-radius: 18px;
   background: linear-gradient(
      160deg,
      rgba(30, 36, 80, 0.72) 0%,
      rgba(20, 24, 56, 0.78) 100%
   );
   backdrop-filter: blur(18px);
   -webkit-backdrop-filter: blur(18px);
   border: 1px solid rgba(255, 255, 255, 0.1);
   box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45),
      0 0 0 1px rgba(255, 255, 255, 0.04) inset,
      0 0 40px rgba(55, 96, 249, 0.15);
   color: white;
   overflow: hidden;

   &::before {
      content: '';
      position: absolute;
      top: -60px;
      right: -60px;
      width: 180px;
      height: 180px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(210, 253, 88, 0.18) 0%, transparent 70%);
      pointer-events: none;
   }
   &::after {
      content: '';
      position: absolute;
      bottom: -80px;
      left: -60px;
      width: 200px;
      height: 200px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(55, 96, 249, 0.22) 0%, transparent 70%);
      pointer-events: none;
   }

   & div:has(> input) {
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.04);
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
   }
   & div:has(> input:focus) {
      border-color: #D2FD58;
      background: rgba(210, 253, 88, 0.04);
      box-shadow: 0 0 0 3px rgba(210, 253, 88, 0.12);
   }
   & input {
      color: white;
      background: transparent;
      caret-color: #D2FD58;
      font-weight: 500;
      letter-spacing: 0.18em;
      text-align: center;
   }
   & input::placeholder { color: rgba(255, 255, 255, 0.35); letter-spacing: normal; }
   & input:-webkit-autofill,
   & input:-webkit-autofill:hover,
   & input:-webkit-autofill:focus {
      -webkit-text-fill-color: white;
      -webkit-box-shadow: 0 0 0 1000px rgba(30, 36, 80, 0.6) inset;
      transition: background-color 9999s ease-in-out 0s;
   }

   & div:has(> div:has(> input)) > div:first-child > p:first-child {
      color: rgba(255, 255, 255, 0.85);
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
   }

   @media (max-width: 768px) {
      width: 100%;
      max-width: 450px;
      padding: 1.25rem;
      border-radius: 16px;
   }
   @media (max-width: 480px) {
      padding: 1rem;
   }
`;

const Title = styled.h2`
   font-size: 26px;
   font-weight: 800;
   text-align: center;
   color: white;
   letter-spacing: -0.01em;

   @media (max-width: 768px) {
      font-size: 22px;
   }
   @media (max-width: 480px) {
      font-size: 19px;
   }
`;

const CheckList = styled.ul`
   margin-top: 1.5rem;
`;

const CheckItem = styled.li`
   display: flex;
   align-items: center;
   gap: 8px;
`;

const CheckLabel = styled.label`
   font-size: 14px;
   color: rgba(255, 255, 255, 0.78);

   @media (max-width: 480px) {
      font-size: 12.5px;
      line-height: 1.4;
   }
`;

const LoginActionContent = styled.div`
   margin-top: 1.25rem;
   margin-bottom: 1rem;
`;

const NewHereWrapper = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 5px;
   margin-top: 1.25rem;

   @media (max-width: 768px) {
      flex-direction: column;
      gap: 8px;
   }
`;

const NewHereLabel = styled.p`
   font-size: 14px;
   color: rgba(255, 255, 255, 0.6);

   @media (max-width: 768px) {
      font-size: 13px;
   }
`;

const NewHereLink = styled.p`
   font-size: 14px;
   color: #D2FD58;
   font-weight: 700;
   cursor: pointer;
   transition: all 0.2s ease;
   &:hover {
      text-decoration: underline;
      filter: brightness(1.1);
   }
`;

const PolicyWrapper = styled.div`
   border-top: 1px solid rgba(255, 255, 255, 0.08);
   padding-top: 1.25rem;
   margin-top: 1.25rem;
`;

const PolicyDescription = styled.p`
   font-size: 12px;
   color: rgba(255, 255, 255, 0.45);
   line-height: 1.5;
   text-align: center;
`;

const PolicyLink = styled.a`
   color: rgba(255, 255, 255, 0.75);
   text-decoration: underline;
   &:hover { color: #D2FD58; }
`;

const PrimaryCTAWrap = styled.div`
   cursor: pointer;
   & > div {
      background: linear-gradient(90deg, #D2FD58 0%, #7de8ff 100%) !important;
      border-radius: 12px;
      box-shadow: 0 10px 26px rgba(210, 253, 88, 0.28),
         0 0 0 1px rgba(210, 253, 88, 0.3) inset;
      transition: filter 0.15s, transform 0.12s;
   }
   & > div p {
      color: #0b1030 !important;
      font-weight: 800 !important;
      letter-spacing: 0.02em;
   }
   &:active > div { transform: translateY(1px); }
   &:hover > div { filter: brightness(1.05); }
`;

const ErrorMessage = styled.p`
   color: #ff6b7a;
   font-size: 13px;
   margin-top: 0.5rem;
   text-align: center;
`;

export default VerifyEmail;
